import { ref, computed, h } from 'vue'
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  useVueTable
} from '@tanstack/vue-table'
import { format, isValid } from 'date-fns'
import { getActionLabel, getLogBookingKindLabel, getLogSubjectId, getLogSubjectLabel } from '../../../../composables/useLogDisplay.js'

const columnHelper = createColumnHelper()

export function useLogsTable(logsGetter) {
  const sorting = ref([])
  const pagination = ref({
    pageIndex: 0,
    pageSize: 25
  })
  const columnFilters = ref([])
  const expanded = ref({})
  const columnVisibility = ref({
    subject: false
  })
  const grouping = ref([])

  const columns = [
    // Expand toggle column
    columnHelper.display({
      id: 'expand',
      header: '',
      enableHiding: false,
      cell: ({ row }) => {
        return h(
          'button',
          {
            onClick: row.getToggleExpandedHandler(),
            class: 'btn btn-sm btn-outline-primary border-0',
            style: { cursor: 'pointer' }
          },
          row.getIsExpanded() 
            ? h('i', { class: 'fas fa-chevron-down' })
            : h('i', { class: 'fas fa-chevron-right' })
        )
      }
    }),
    columnHelper.accessor(row => getLogSubjectId(row), {
      id: 'subject',
      header: 'Buchung/Serie',
      cell: ({ row }) => getLogSubjectLabel(row.original)
    }),
    columnHelper.accessor('logDate', {
      header: 'Datum',
      cell: info => {
        const date = info.getValue()
        if (!date) return '-'
        const parsed = new Date(date)
        return isValid(parsed) ? format(parsed, 'dd.MM.yyyy HH:mm') : 'Invalid Date'
      }
    }),
    columnHelper.accessor('action', {
      header: 'Aktion',
      cell: ({ getValue }) => {
        const action = getValue()
        const badgeClass = {
          created: 'badge bg-success',
          changed: 'badge bg-warning',
          cancelled: 'badge bg-danger',
          extracted: 'badge bg-info'
        }
        return h('span', { class: badgeClass[action] || 'badge bg-secondary' }, getActionLabel(action))
      }
    }),
    columnHelper.accessor('user', {
      header: 'Benutzer',
      cell: info => {
        const user = info.getValue()
        return user ? user.displayName || user.username : '-'
      }
    }),
    columnHelper.accessor('room', {
      header: 'Raum',
      cell: info => {
        const room = info.getValue()
        if (!room) return '-'
        
        // If we have building abbreviation and door label, format as "LP - 09"
        if (room.building?.short && room.doorLabel) {
          return `${room.building.short} - ${room.doorLabel}`
        }
        
        // Fallback to just door label if available
        if (room.doorLabel) {
          return room.doorLabel
        }
        
        // Final fallback if room doesn't exist (shouldn't happen)
        return `Raum #${room.id}`
      },
      getGroupingValue: (row) => {
        const room = row.room
        if (!room) return 'Kein Raum'
        
        // Use the same logic as the cell renderer for consistent grouping
        if (room.building?.short && room.doorLabel) {
          return `${room.building.short} - ${room.doorLabel}`
        }
        
        if (room.doorLabel) {
          return room.doorLabel
        }
        
        return `Raum #${room.id}`
      }
    }),
    columnHelper.accessor(row => row.changes?.new?.title || row.changes?.old?.title, {
      id: 'title',
      header: 'Titel',
      cell: info => info.getValue() || '-'
    }),
    columnHelper.accessor(row => row.changes?.new?.bookingTypeData || row.changes?.old?.bookingTypeData, {
      id: 'bookingType',
      header: 'Typ',
      cell: info => {
        const bookingType = info.getValue()
        if (!bookingType) return '-'
        return h('span', {
          class: 'badge',
          style: {
            backgroundColor: bookingType.backgroundColor || '#6c757d',
            color: bookingType.textColor || '#ffffff'
          }
        }, bookingType.title || '-')
      }
    }),
    columnHelper.accessor(row => getLogBookingKindLabel(row), {
      id: 'bookingKind',
      header: 'Buchungstyp',
      cell: info => info.getValue()
    }),
    columnHelper.accessor(row => {
      const newStart = row.changes?.new?.startTime
      const oldStart = row.changes?.old?.startTime
      const newEnd = row.changes?.new?.endTime
      const oldEnd = row.changes?.old?.endTime
      const startTime = newStart || oldStart
      const endTime = newEnd || oldEnd
      return { startTime, endTime }
    }, {
      id: 'timeRange',
      header: 'Zeitraum',
      cell: info => {
        const { startTime, endTime } = info.getValue()
        if (!startTime || !endTime) return '-'
        const startDate = new Date(startTime)
        const endDate = new Date(endTime)
        const start = isValid(startDate) ? format(startDate, 'dd.MM.yyyy HH:mm') : 'Invalid Date'
        const end = isValid(endDate) ? format(endDate, 'HH:mm') : 'Invalid Date'
        return `${start} - ${end}`
      }
    })
  ]

  const data = computed(() => {
    const logs = typeof logsGetter === 'function' ? logsGetter() : logsGetter
    return logs || []
  })

  const table = useVueTable({
    get data() { return data.value },
    columns,
    state: {
      get sorting() { return sorting.value },
      get pagination() { return pagination.value },
      get columnFilters() { return columnFilters.value },
      get expanded() { return expanded.value },
      get columnVisibility() { return columnVisibility.value },
      get grouping() { return grouping.value }
    },
    onSortingChange: updaterOrValue => {
      sorting.value = typeof updaterOrValue === 'function' 
        ? updaterOrValue(sorting.value) 
        : updaterOrValue
    },
    onPaginationChange: updaterOrValue => {
      pagination.value = typeof updaterOrValue === 'function' 
        ? updaterOrValue(pagination.value) 
        : updaterOrValue
    },
    onColumnFiltersChange: updaterOrValue => {
      columnFilters.value = typeof updaterOrValue === 'function' 
        ? updaterOrValue(columnFilters.value) 
        : updaterOrValue
    },
    onExpandedChange: updaterOrValue => {
      expanded.value = typeof updaterOrValue === 'function' 
        ? updaterOrValue(expanded.value) 
        : updaterOrValue
    },
    onColumnVisibilityChange: updaterOrValue => {
      columnVisibility.value = typeof updaterOrValue === 'function' 
        ? updaterOrValue(columnVisibility.value) 
        : updaterOrValue
    },
    onGroupingChange: updaterOrValue => {
      grouping.value = typeof updaterOrValue === 'function' 
        ? updaterOrValue(grouping.value) 
        : updaterOrValue
    },
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    enableGrouping: true,
    manualFiltering: true // Server-side filtering
  })

  return {
    table,
    sorting,
    pagination,
    columnFilters,
    expanded,
    columnVisibility,
    grouping
  }
}